<script setup lang="ts">
// Vue
import { computed } from "vue";

// Types
import { LifetimeTableRow } from "@/providers/LifetimeTable/LifetimeTableTypes";

// Utils
import BIMFormat from "@/services/Utils/BIMFormat";

// ///////////////////
// Props
// ///////////////////

const props = defineProps<{
  row: LifetimeTableRow;
  rowIndex: number;
  focused: boolean;
  focusedAlternate: boolean;
  headers: string[] | undefined;
  hoveredRowIndex: number | null;
  hoveredColIndex: number | null;
}>();

// ///////////////////
// Emits
// ///////////////////

const emits = defineEmits(["lifeTimeSelected", "cellHover"]);

// ///////////////////
// Computed
// ///////////////////

const isRowHovered = computed(() => props.hoveredRowIndex === props.rowIndex);

const rowLabelBg = computed(() => {
  if (props.focused) return "bg-app-green-100!";
  if (props.focusedAlternate) return "bg-app-yellow-100!";
  if (isRowHovered.value) return "bg-app-blue-50!";
  return "";
});

// ///////////////////
// Methods
// ///////////////////

const getCellBg = (colIndex: number) => {
  if (props.focused) return "bg-app-green-100";
  if (props.focusedAlternate) return "bg-app-yellow-100";
  if (isRowHovered.value || props.hoveredColIndex === colIndex) return "bg-app-blue-50";
  return "";
};

const handleLifeTimeSelected = (value: number, constructionIdAlternate: string) => {
  emits("lifeTimeSelected", { value, constructionIdAlternate });
};

const handleCellEnter = (colIndex: number) => {
  emits("cellHover", { rowIndex: props.rowIndex, colIndex });
};
</script>

<template>
  <td
    :id="`#${BIMFormat(row.constructionId)}`"
    class="z-index sticky! left-0 align-bottom"
    :class="[rowLabelBg]"
    @mouseenter="handleCellEnter(-1)">
    <div class="flex w-72 text-sm">
      <div
        :class="[rowLabelBg]"
        class="bg-surface absolute top-0 left-2 flex h-full w-72 items-center pr-4 whitespace-nowrap hover:w-auto hover:overflow-visible">
        <span class="overflow-hidden text-ellipsis">{{ row.constructionId + " " + row.name }}</span>
      </div>
    </div>
  </td>
  <td
    v-for="(header, colIndex) in headers"
    :key="header"
    class="text-center text-sm"
    :class="[row.materialLifeTime[header] ? 'hover:bg-element-stroke cursor-pointer transition-all' : '', getCellBg(colIndex)]"
    @mouseenter="handleCellEnter(colIndex)"
    @click="
      row.materialLifeTime[header]
        ? handleLifeTimeSelected(row.materialLifeTime[header], BIMFormat(row.constructionId))
        : undefined
    ">
    {{ row.materialLifeTime[header] ? row.materialLifeTime[header] : "" }}
  </td>
</template>

<style scoped>
td {
  padding: 8px;
  outline: 1px solid var(--color-element-stroke); /* migrated to token – was #e3e8ee */
}
tbody td:first-child {
  padding-top: 0;
  padding-bottom: 0;
  position: sticky !important;
  z-index: 10;
  left: 0;
  background-color: var(--color-surface); /* migrated to token – was #f3f6f5 */
}
</style>
